import React, { useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Paper, Typography, TextField, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import MonitorNavigation from '../../components/monitor/MonitorNavigation';
import useWipFirestoreStock from '../../hooks/useWipFirestoreStock';
import { buildWipMonitorSummary, formatMonitorNumber } from '../../utils/wipMonitor';

export default function LowCare({ embedded = false }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { items, loading, error, clock, scopeReady } = useWipFirestoreStock();
  const [zone, setZone] = useState('');
  const visibleItems = useMemo(() => items.filter(item => !zone || item.zone === zone), [items, zone]);
  const summary = useMemo(() => buildWipMonitorSummary(visibleItems), [visibleItems]);
  const categories = ['RAW MAT', 'WIP', 'FG'].map(name => {
    const rows = visibleItems.filter(item => item.category === name);
    return { name, totalKg: rows.reduce((sum, row) => sum + row.weightKg, 0),
      sku: new Set(rows.map(row => row.sku)).size, batch: new Set(rows.map(row => row.lot + '|' + row.batch)).size,
      expired: rows.filter(row => row.status === 'EXPIRED').length, mustUse: rows.filter(row => row.status === 'MUST USE').length };
  });


  const panel = { p: 1.5, bgcolor: '#1b222b', border: '1px solid #344b59', borderRadius: 2, color: '#edf4f7', minWidth: 0 };
  const statusColor = { EXPIRED: '#ff8585', 'MUST USE': '#ffd166', READY: '#70cbd2', PREPARING: '#8cdda5' };
  const text = { fontSize: 16, lineHeight: 1.4, color: '#c1d0d9' };
  return <Box sx={{ bgcolor: '#0e161e', p: { xs: 1.5, md: 2 } }}>
    {!embedded && <MonitorNavigation compact />}
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
      <Typography component="h1" sx={{ fontSize: 28, color: colors.primary[500], fontWeight: 600, mr: 'auto' }}>Warehouse</Typography>
      {!embedded && <TextField select label="โซน" size="small" value={zone} onChange={e => setZone(e.target.value)}
        SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
        sx={{ width: 180, '& .MuiInputBase-root': { height: 46, fontSize: 18, bgcolor: '#182530', color: '#edf4f7' },
          '& .MuiInputLabel-root, & .MuiSvgIcon-root': { color: '#bdcbd6' },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#526978' } }}>
        <MenuItem value="">ทุกโซน</MenuItem><MenuItem value="ดิบ">ดิบ</MenuItem><MenuItem value="สุก">สุก</MenuItem>
      </TextField>}
      <Typography sx={text}>{clock.toLocaleString('en-GB')}</Typography>
    </Box>
    {!scopeReady && <Alert severity="warning" sx={{ mb: 1 }}>กรุณาเลือก Plant และ Factory</Alert>}
    {error && <Alert severity="error" sx={{ mb: 1 }}>ไม่สามารถโหลดข้อมูลได้: {error}</Alert>}
    {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5, mb: 1.5 }}>
        <Paper sx={{ ...panel, borderColor: colors.primary[500], display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 20, color: colors.primary[500], mb: 1 }}>Total Storage</Typography>
          <Typography sx={{ fontSize: 36, fontWeight: 600, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{formatMonitorNumber(summary.totalKg)} <Box component="span" sx={text}>kg</Box></Typography>
          <Typography sx={{ ...text, mt: 1 }}>{visibleItems.length} รายการ · {zone || 'ทุกโซน'}</Typography>
        </Paper>
        {categories.map(area => <Paper key={area.name} sx={panel}>
          <Typography sx={{ fontSize: 20, color: colors.primary[500], mb: 1 }}>{area.name === 'RAW MAT' ? 'Raw Mat' : area.name}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 1 }}>
            {[['น้ำหนัก kg', area.totalKg], ['SKU', area.sku], ['Batch', area.batch]].map(([name, value]) =>
              <Box key={name} sx={{ minWidth: 0 }}><Typography sx={text}>{name}</Typography><Typography sx={{ fontSize: 28, lineHeight: 1.2, overflowWrap: 'anywhere' }}>{formatMonitorNumber(value)}</Typography></Box>)}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, borderTop: '1px solid #344b59', pt: 1, mt: 1 }}>
            <Typography sx={{ ...text, color: statusColor.EXPIRED }}>Expired <b>{area.expired}</b></Typography>
            <Typography sx={{ ...text, color: statusColor['MUST USE'] }}>Must Use <b>{area.mustUse}</b></Typography>
          </Box>
        </Paper>)}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 2fr)' }, gap: 1.5, alignItems: 'stretch' }}>
        <Paper sx={panel}>
          <Typography component="h2" sx={{ fontSize: 20, mb: 1 }}>Aging Status</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
            {summary.agingStatus.map(item => <Box key={item.status} sx={{ bgcolor: '#111b24', p: 1, borderRadius: 1 }}>
              <Typography sx={{ ...text, color: statusColor[item.status] }}>{item.status}</Typography>
              <Typography sx={{ fontSize: 28, lineHeight: 1.2 }}>{item.count} <Box component="span" sx={text}>SKU · {item.percent}%</Box></Typography>
            </Box>)}
          </Box>
        </Paper>
        <Paper sx={panel}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography component="h2" sx={{ fontSize: 20 }}>Aging Alert</Typography>
            <Typography sx={text}>6 รายการตามความเร่งด่วน</Typography>
          </Box>
          {!summary.agingAlerts.length ? <Typography sx={{ ...text, textAlign: 'center', py: 3 }}>ยังไม่มีสต็อกในโซนที่เลือก</Typography> :
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
            {summary.agingAlerts.map(item => <Box key={item.id} sx={{ minWidth: 0, p: 1, bgcolor: '#111b24', borderRadius: 1, borderLeft: '3px solid ' + statusColor[item.status] }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography noWrap title={item.sku + ' · ' + item.skuName} sx={{ fontSize: 18, minWidth: 0 }}>{item.sku}</Typography>
                <Typography sx={{ ...text, color: statusColor[item.status], whiteSpace: 'nowrap' }}>{item.status}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography noWrap title={item.location} sx={{ ...text, minWidth: 0 }}>{item.location}</Typography>
                <Typography sx={{ ...text, whiteSpace: 'nowrap' }}>{formatMonitorNumber(item.agingHours, 1)} ชม.</Typography>
              </Box>
            </Box>)}
          </Box>}
        </Paper>
      </Box>
    </>}
  </Box>;
}
